import { z } from 'zod';

export const ConsumerRegisterBodySchema = z.object({
  /** CPF (11 digits) or CNPJ (14 digits); accepts raw digits or formatted (mask up to 18 chars). */
  document: z.string().min(11).max(18),
  email: z.email(),
  password: z.string().min(8),
  firstName: z.string().trim().min(1).max(80),
  lastName: z.string().trim().min(1).max(80),
  termsAccepted: z.literal(true),
  privacyAccepted: z.literal(true),
  treatmentAck: z.literal(true),
  termsVersion: z.string().min(1),
});
export type ConsumerRegisterBody = z.infer<typeof ConsumerRegisterBodySchema>;

export const ConsumerRegisterResponseSchema = z.object({
  consumerId: z.uuid(),
  status: z.literal('pending_email'),
});
export type ConsumerRegisterResponse = z.infer<typeof ConsumerRegisterResponseSchema>;

export const ConsumerConfirmEmailBodySchema = z.object({
  token: z.string().regex(/^\d{6}$/),
});
export type ConsumerConfirmEmailBody = z.infer<typeof ConsumerConfirmEmailBodySchema>;

export const ConsumerConfirmEmailResponseSchema = z.object({
  consumerId: z.uuid(),
  status: z.literal('active'),
});
export type ConsumerConfirmEmailResponse = z.infer<typeof ConsumerConfirmEmailResponseSchema>;

export const ConsumerLoginBodySchema = z.object({
  identifier: z.string().min(1),
  password: z.string().min(1),
});
export type ConsumerLoginBody = z.infer<typeof ConsumerLoginBodySchema>;

export const ConsumerRefreshBodySchema = z.object({
  refreshToken: z.string().min(1).optional(),
});
export type ConsumerRefreshBody = z.infer<typeof ConsumerRefreshBodySchema>;

export const ConsumerForgotPasswordBodySchema = z.object({
  identifier: z.string().min(1),
});
export type ConsumerForgotPasswordBody = z.infer<typeof ConsumerForgotPasswordBodySchema>;

export const ConsumerForgotPasswordResponseSchema = z.object({
  accepted: z.literal(true),
});
export type ConsumerForgotPasswordResponse = z.infer<typeof ConsumerForgotPasswordResponseSchema>;

export const ResolveConsumerPasswordResetTargetBodySchema = z.object({
  document: z.string().min(1),
});
export type ResolveConsumerPasswordResetTargetBody = z.infer<
  typeof ResolveConsumerPasswordResetTargetBodySchema
>;

export const ResolveConsumerPasswordResetTargetResponseSchema = z.discriminatedUnion('found', [
  z.object({ found: z.literal(true), maskedEmail: z.string() }),
  z.object({ found: z.literal(false) }),
]);
export type ResolveConsumerPasswordResetTargetResponse = z.infer<
  typeof ResolveConsumerPasswordResetTargetResponseSchema
>;

export const ConsumerResendConfirmationBodySchema = z.object({
  email: z.email(),
});
export type ConsumerResendConfirmationBody = z.infer<typeof ConsumerResendConfirmationBodySchema>;

export const ConsumerResendConfirmationResponseSchema = z.object({
  accepted: z.literal(true),
});
export type ConsumerResendConfirmationResponse = z.infer<
  typeof ConsumerResendConfirmationResponseSchema
>;

export const ConsumerResetPasswordBodySchema = z.object({
  token: z.string().regex(/^\d{6}$/),
  newPassword: z.string().min(8),
});
export type ConsumerResetPasswordBody = z.infer<typeof ConsumerResetPasswordBodySchema>;

export const ConsumerResetPasswordResponseSchema = z.object({
  consumerId: z.uuid(),
  status: z.literal('active'),
});
export type ConsumerResetPasswordResponse = z.infer<typeof ConsumerResetPasswordResponseSchema>;

export const ConsumerAuthSessionResponseSchema = z.object({
  accessToken: z.string().min(1),
  /** Raw refresh token for native clients (Secure Store); also set as HttpOnly cookie for web. */
  refreshToken: z.string().min(1),
  expiresIn: z.number().int().positive(),
  tokenType: z.literal('Bearer'),
  consumerId: z.uuid(),
  principalType: z.literal('consumer'),
});
export type ConsumerAuthSessionResponse = z.infer<typeof ConsumerAuthSessionResponseSchema>;

export const ConsumerAuthMeResponseSchema = z.object({
  consumerId: z.uuid(),
  email: z.email(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  status: z.enum(['pending_email', 'active', 'archived']),
  principalType: z.literal('consumer'),
});
export type ConsumerAuthMeResponse = z.infer<typeof ConsumerAuthMeResponseSchema>;
